import {Component, ElementRef, HostListener, Input, OnInit, OnDestroy, Pipe, PipeTransform, ViewChild} from '@angular/core';
import {QuestionBase} from "../../pages/create-adv/models/question-base";
import {ApiUrl} from "../../shared/models/apiurl.models";
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {AdvFormService} from "../../pages/create-adv/services/adv-form.service";
import {LoadingMaskServiceService} from "../loading-frame/loading-frame.component";
import {SystemApp} from "../../constans/SystemConfigurations";
import {Observable, Subject, throwError} from "rxjs";
import {catchError, debounceTime, distinctUntilChanged, map, switchMap, takeUntil} from "rxjs/operators";
import {selectOptionData} from "../../pages/create-adv/dynamic-form-question/dynamic-form-question.component";
import {StringUtils} from "../../utils/StringUtils";
import {ApiResponseUtils} from "../../utils/api_response_utils";


@Pipe({
    name: 'filter',
    standalone: false
})
export class FilterPipe implements PipeTransform {

  transform(items: any[], searchText: string, fieldName: string): any[] {
    // return empty array if array is falsy
    if (!items) { return []; }

    // return the original array if search text is empty
    if (!searchText) { return items; }

    // convert the searchText to lower case
    searchText = searchText.toLowerCase();

    // return the filtered array
    return items.filter(item => {
      if (item && item[fieldName]) {
        return item[fieldName].toLowerCase().includes(searchText);
      }
      return false;
    });
  }
}

@Component({
    selector: 'app-ui-select-dropdown',
    templateUrl: './ui-select-dropdown.component.html',
    styleUrls: ['./ui-select-dropdown.component.css'],
    standalone: false
})
export class UiSelectDropdownComponent implements OnInit, OnDestroy {

  pointerIndex = -1;

  /** Subject to drive debounced search — replaces per-keystroke HTTP calls */
  private searchSubject = new Subject<string>();

  /** Emits once on destroy to auto-unsubscribe everything */
  private destroy$ = new Subject<void>();

  selectBtBtn(event: KeyboardEvent) {}

  getClass(item: any) {
    if (this.selectedItem === item) {
      return 'selectedRow';
    }
    return '';
  }

  /** trackBy for *ngFor — avoids re-creating DOM nodes when the list refreshes */
  trackByOption(_index: number, opt: any): any {
    return opt?.value ?? opt?.id ?? opt?.label ?? _index;
  }

  @Input() questionBase!: QuestionBase<any>;
  @ViewChild('optionsList') optionsList!: ElementRef;
  expanded: boolean = false;
  dataList: any = [];
  fetchUrl!: string;
  inputValue!: string;
  translatedQue!: any;
  isLoading: boolean = false;
  selectedItem: any;
  private onBlurtimeoutId!: number;

  // ─── Multi-select state ───────────────────────────────────────────

  /** True when controlType === 'SelectField' and type === 'multiSelect' */
  isMultiSelect: boolean = false;

  /** Full objects currently selected in multi-select mode */
  selectedItems: any[] = [];

  /** Labels of selected items — passed to the child for chip display */
  get selectedItemLabels(): string[] {
    return this.selectedItems.map(i => i?.label ?? '');
  }

  // ──────────────────────────────────────────────────────────────────

  /** Build headers lazily so the JWT token is always fresh */
  private get httpOptions() {
    return {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': this.apiUrl.getJWTToken()
      })
    };
  }

  constructor(
      private apiUrl: ApiUrl,
      private http: HttpClient,
      private formService: AdvFormService,
      private loadingMaskServiceService: LoadingMaskServiceService
  ) {
    // takeUntil ensures this subscription is cleaned up on destroy
    this.formService.getClickEvent()
        .pipe(takeUntil(this.destroy$))
        .subscribe((res: any) => {
          if (res != this.questionBase.key) return;
          this.inputValue = "";
          this.dataList = [];
          // Reset expanded too — onClick() early-returns when expanded is
          // already true (guards against re-fetching while the panel is
          // open), but that flag was never cleared on a parent-driven reset.
          // A cascading child (e.g. Internalads' linked_sconderyCategory)
          // that had already been opened once would silently stop
          // re-fetching for every subsequent parent (linked_mainCategory)
          // change: clearRelatedFields()->deleteQuestion() fires this same
          // click event and cleared dataList/inputValue, but with expanded
          // stuck true the next click on the child no-opped, leaving it
          // looking permanently empty until a full page reload. Confirmed
          // via a live browser test switching linked_mainCategory between
          // two categories.
          this.expanded = false;
          // Also clear multi-select state
          if (this.isMultiSelect) {
            this.selectedItems = [];
          }
        });
  }

  initData() {
    // ── Detect multi-select mode ──
    this.isMultiSelect =
        (this.questionBase.controlType === 'SelectField' || this.questionBase.controlType === 'dropdown') &&
        this.questionBase.type === 'multiSelect';

    if (this.isMultiSelect) {
      this.initMultiSelectData();
    } else {
      this.initSingleSelectData();
    }
  }

  /** Original single-select initialisation — unchanged */
  private initSingleSelectData() {
    if (this.questionBase.value && typeof this.questionBase.value === 'object') {
      this.selectedItem = this.questionBase.value;
      this.inputValue = this.questionBase.value.label;
      this.formService.addQuestion(this.questionBase.key, this.questionBase.value);

      if (!this.dataList || this.dataList.length === 0) {
        this.dataList = [this.selectedItem];
      } else {
        const exists = this.dataList.find(i => i.id === this.selectedItem.id);
        if (!exists) {
          this.dataList.unshift(this.selectedItem);
        }
      }
    } else {
      if (this.questionBase.value != 'None') {
        this.inputValue = this.questionBase.value;
      }
    }
  }

  /** Multi-select initialisation — restores an array of pre-selected objects */
  private initMultiSelectData() {
    const value = this.questionBase.value;

    if (Array.isArray(value) && value.length > 0) {
      this.selectedItems = [...value];
      this.formService.addQuestion(this.questionBase.key, this.selectedItems);

      // Seed the dataList so the child component can show the labels
      if (!this.dataList || this.dataList.length === 0) {
        this.dataList = [...this.selectedItems];
      } else {
        // Merge any missing selected items into the list
        for (const item of this.selectedItems) {
          const exists = this.dataList.find(i => i.id === item.id);
          if (!exists) {
            this.dataList.unshift(item);
          }
        }
      }
    }
  }

  ngOnInit(): void {
    this.initData();
    this.fetchUrl = SystemApp.base_url_api + '/' + this.questionBase.callBackFunction.$piece("*", 1);

    // ── Debounced search pipeline ──
    this.searchSubject.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(query => this.fetchData(this.fetchUrl, query)),
        takeUntil(this.destroy$)
    ).subscribe(res => {
      const fetched = ApiResponseUtils.extractList(res);

      if (this.isMultiSelect) {
        // Merge already-selected items into the fetched results so they
        // remain visible in the dropdown (with a checkmark) even if the
        // server didn't return them for this query.
        const fetchedIds = new Set(fetched.map(i => i.id));
        const missing = this.selectedItems.filter(i => !fetchedIds.has(i.id));
        this.dataList = [...missing, ...fetched];
      } else {
        this.dataList = fetched;
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    clearTimeout(this.onBlurtimeoutId);
  }

  onClick() {
    if (this.expanded == true) { return; }
    this.isLoading = true;
    // 4th arg (questionBase itself) was previously omitted here, which
    // silently disabled apiUrl.pathTranslate()'s parentMapValue-driven
    // cascading param resolution (parseQueryParams()/resolveParamValue()
    // need the question to read its own parentMapValue off) — every
    // dependent SelectField (e.g. Internalads' linked_sconderyCategory,
    // cascading off linked_mainCategory) silently fetched an unfiltered
    // list instead. Safe to pass now: no existing callBackFunction in any
    // dynamicForms seed embeds a '?' in its path, so parseQueryParams()
    // never actually ran for any pre-existing category/question — this
    // only takes effect for callBackFunctions that opt into it.
    this.translatedQue = this.apiUrl.pathTranslate(this.questionBase.callBackFunction, this.formService, this.questionBase.additionalParam, this.questionBase);
    this.fetchUrl = this.translatedQue.path;
    this.fetchData(this.fetchUrl, this.inputValue)
        .pipe(takeUntil(this.destroy$))
        .subscribe(res => {
          const fetched = ApiResponseUtils.extractList(res);

          if (this.isMultiSelect) {
            // Same merge logic — keep selected items visible
            const fetchedIds = new Set(fetched.map(i => i.id));
            const missing = this.selectedItems.filter(i => !fetchedIds.has(i.id));
            this.dataList = [...missing, ...fetched];
          } else {
            this.dataList = fetched;
          }

          this.expanded = true;
          this.isLoading = false;
        });
  }

  onBlur($event: any) {
    // @ts-ignore
    this.onBlurtimeoutId = setTimeout(() => { this.expanded = false; }, 100);
  }

  fetchData(url: string, query: string = ''): Observable<any> {
    if (query != null && query != undefined && query != '') {
      if (url.includes("?")) {
        url = url + "&q=" + query;
      } else {
        url = url + "?q=" + query;
      }
    }
    return this.http.get(url, this.httpOptions)
        .pipe(
            map((res) => res ? res : null),
            catchError(this.handleObserverError)
        );
  }

  private handleObserverError(error: any): Observable<any> {
    return throwError(error.error || error);
  }

  /**
   * Handles the child's onChange event.
   *
   * Single-select: value is a label string.
   * Multi-select:  value is an array of label strings.
   */
  onChange(value: any) {
    if (this.isMultiSelect) {
      this.onMultiSelectChange(value);
    } else {
      this.onSingleSelectChange(value);
    }
  }

  /** Original single-select handler — unchanged */
  private onSingleSelectChange(value: any) {
    this.selectedItem = this.dataList.find(i => i.label == value);
    this.selectValue(value, this.selectedItem);
    this.inputValue = value;
  }

  /** Multi-select handler — maps labels back to full objects */
  private onMultiSelectChange(labels: string[]) {
    this.selectedItems = (labels || [])
        .map(label => this.dataList.find(i => i.label === label))
        .filter(Boolean);

    this.formService.addQuestion(this.questionBase.key, this.selectedItems);
    this.formService.clearRelatedFields(this.questionBase);
  }

  stopPropagation($event: any): void {
    $event.stopPropagation();
    $event.preventDefault();
    clearTimeout(this.onBlurtimeoutId);
  }

  selectValue($event1: any, $event: selectOptionData) {
    this.inputValue = $event?.label;
    const index = this.questionBase.key;
    this.formService.addQuestion(index, $event);
    this.formService.clearRelatedFields(this.questionBase);
    // @ts-ignore
    this.onBlurtimeoutId = setTimeout(() => { this.expanded = false; }, 50);
  }

  onSearch($event: any) {
    // Push into the debounced pipeline instead of firing HTTP directly
    this.searchSubject.next($event);
  }
}
